import { useEffect, useState } from 'react';

import { AddMerchantApplication, ApplicationStatus, ServiceType, User } from '@prisma/client';

import { useQuery } from '@redwoodjs/web';

import { PillarHeader } from 'src/components';
import Loader from 'src/components/Loader/Loader';
import ManageSignature from 'src/components/ServiceOrderSignature/ManageSignature';
import SignatureStatus from 'src/components/ServiceOrderSignature/SignatureStatus';
import { GET_APPLICATION } from 'src/graphql/queries/merchantApplications.query';
import useAgreementStatus from 'src/hooks/useAgreementStatus';
import AuthorServiceDetails from 'src/pages/MerchantApplicationPage/components/AuthorServiceDetails';
interface ServiceOrderManagementDashboardProps {
    serviceApplication: AddMerchantApplication & {
        author: User;
        terminalId: string;
    };
    merchant: {
        merchantName: string;
        mid: string;
        signer: {
            firstName: string;
            lastName: string;
            email: string;
        };
    };
    fetchingApplication: boolean;
    completing: boolean;
    serviceApplicationStatus?: ApplicationStatus;
    serviceApplicationID?: number;
    isAgreementSigned?: boolean;
    onApplicationStatusUpdate: (status: ApplicationStatus) => void;
    handleCompleteOrder: (status: ApplicationStatus) => void;
    processingRequestBuild: boolean;
    handleProcessServiceBuild: ({ mid, terminalId }: { mid: string; terminalId: string }) => void;
    onResendAgreement: () => void;
    resendingLoading: boolean;
    isResendDisabled: boolean;
    handleRefresh: () => void;
    isRefreshing: boolean;
    handleContinueClick?: () => void;
    agreementPreviewUrl?: string;
    onCaptureOnScreen: () => void;
}

const ServiceOrderManagementDashboard = ({
    serviceApplication,
    merchant,
    fetchingApplication,
    completing,
    serviceApplicationStatus,
    serviceApplicationID,
    isAgreementSigned,
    onApplicationStatusUpdate,
    handleCompleteOrder,
    processingRequestBuild,
    handleProcessServiceBuild,
    onResendAgreement,
    resendingLoading,
    isResendDisabled,
    handleRefresh,
    isRefreshing,
    handleContinueClick,
    agreementPreviewUrl,
}: ServiceOrderManagementDashboardProps) => {
    const { merchantName, signer, mid } = merchant;
    const [terminalId, setTerminalId] = useState('');
    const [latestStatus, setLatestStatus] = useState<ApplicationStatus>(serviceApplicationStatus);

    const { getAgreementStatus, previewUrl, refetchAgreementStatus } = useAgreementStatus({
        applicationId: serviceApplicationID,
        onAgreementStatusCompleted: () => {
            refetchAgreementStatus();
        },
    });

    const applicationId = serviceApplicationID;
    const { data } = useQuery(GET_APPLICATION, {
        fetchPolicy: 'network-only',
        variables: {
            id: applicationId,
        },
        pollInterval: latestStatus === ApplicationStatus.AWAITING_SIGNATURE ? 10000 : 0,
        skip: !applicationId,
        onCompleted: () => {
            refetchAgreementStatus();
        },
    });

    useEffect(() => {
        const newStatus = data?.retrieveMerchantApplication?.status;

        if (newStatus && newStatus !== latestStatus) {
            setLatestStatus(newStatus);
            onApplicationStatusUpdate(newStatus);
        }

        if (serviceApplicationID) {
            getAgreementStatus({ applicationId: serviceApplicationID });
        }
    }, [data, latestStatus, serviceApplicationID, getAgreementStatus, onApplicationStatusUpdate]);

    return (
        <>
            {fetchingApplication || completing ? <Loader /> : null}
            <PillarHeader
                heading={merchantName}
                subheading={`Service Order ID ${serviceApplicationID}`}
                sx={{
                    backgroundImage: 'url(/assets/images/backgrounds/Table.jpg)',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                }}
            />
            <AuthorServiceDetails application={serviceApplication} />
            <SignatureStatus
                label="Agreement Status"
                status={latestStatus}
                onApplicationStatusUpdate={onApplicationStatusUpdate}
                handleCompleteOrder={handleCompleteOrder}
                processingRequestBuild={processingRequestBuild}
                handleProcessServiceBuild={handleProcessServiceBuild}
                terminalId={terminalId}
                mid={mid}
                handleContinueClick={handleContinueClick}
                agreementPreviewUrl={previewUrl || agreementPreviewUrl}
                isRefreshing={isRefreshing || fetchingApplication || completing}
                serviceApplication={serviceApplication}
            />
            <ManageSignature
                signer={signer}
                isSigned={isAgreementSigned}
                mid={mid}
                serviceApplicationStatus={latestStatus}
                setTerminalId={setTerminalId}
                onResendAgreement={onResendAgreement}
                resendingLoading={resendingLoading}
                isResendDisabled={isResendDisabled}
                handleRefresh={handleRefresh}
                isRefreshing={isRefreshing}
                isOnlineServiceType={
                    (
                        serviceApplication.serviceRequirements as {
                            service?: { serviceType?: ServiceType };
                        }
                    )?.service?.serviceType === ServiceType.ONLINE
                }
                tid={serviceApplication.terminalId}
            />
        </>
    );
};

export default ServiceOrderManagementDashboard;
